<template>
  <div class="info-box text-left pa mb">
    <h2 class="text-grey mb">{{ t("CreateAccount.welcomeHeading") }}</h2>
    <p class="">{{ tn("welcomeAolP1") }}</p>
    <p class="">{{ tn("welcomeAolP2") }}</p>
  </div>

  <h1>{{ t("DownloadsComponent.GetStartedHeader") }}</h1>

  <form>
    <ul class="form mb-100">
      <li>
        <label for="aolEmail">
          {{ tn("emailLabel") }}
        </label>
        <InputError :show="emailError !== null">
          <input id="aolEmail" v-model="userMail" type="text" @keyup="dirtyFlagEmail = true" />
          <template #error>
            <div>
              <span :key="emailError?.name">{{ emailError?.message || t(emailError?.name ?? "") }}</span>
            </div>
          </template>
        </InputError>
      </li>
      <li v-if="showV2Recaptcha">
        <div class="captcha mb">
          <vue-recaptcha
            :key="captchaV2ComponentKey"
            :sitekey="captchaV2SiteKey"
            :load-recaptcha-script="true"
            :language="recaptchaLang"
            @verify="captchaV2Callback"
            @expired="onCaptchaV2Expired"
          ></vue-recaptcha>
        </div>
      </li>
      <li>
        <button class="btn-primary" :disabled="isVerifying || emailError != null" @click="checkUserEmail()">
          {{ t("Common.continue") }}
          <spinner :is-spinning="isVerifying" />
        </button>
      </li>
    </ul>
  </form>
</template>

<script setup lang="ts">
import { t } from "@/i18n";
import { logEvent } from "@/common/logger";
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import { useConfigStore } from "@/stores/config";
import InputError from "@/components/shared/InputError.vue";
import { VueRecaptcha } from "vue-recaptcha";
import { ReCaptchaInstance } from "recaptcha-v3";
import { getReCaptcha, updateReCaptchaLanguage } from "@/common/ReCaptchaV3";
import { useUserStore } from "@/stores/user";
import { checkForUserAccount } from "./AolCreateAccountHelper";
import { isValidEmail } from "@/common/validateEmail";
import { IValidationResult, reportStringErrors } from "@/common/validator";
import { MAX_EMAIL_LENGTH, MIN_EMAIL_LENGTH } from "@/define";
import { emailCharactersRegex } from "@/globalRegex";
import Spinner from "@/components/shared/Spinner.vue";
import { useAolAccountStore } from "@/stores/AolAccount";

const componentName = "GetStarted";
const baseComponentName = "CreateAolAccount";

const configStore = useConfigStore();
const userStore = useUserStore();

const dirtyFlagEmail = ref<boolean>(false);
const recaptchaInstance = ref<ReCaptchaInstance>();
const showV2Recaptcha = ref<boolean>(false);
const captchaV2ComponentKey = ref<number>(0);
const captchaV2SiteKey = ref<string>("");
const recaptchaLang = ref<string>(localStorage.getItem("selectedLanguage") || "en");
const captchaV2ResponseToken = ref<string>("");
const isVerifying = ref<boolean>(false);
const userMail = ref<string>("");
const aolAccountStore = useAolAccountStore();

logEvent("created", componentName);

onBeforeMount(async () => {
  await configStore.getConfig();
  captchaV2SiteKey.value = configStore.config?.captcha?.siteKey ?? "";
  recaptchaInstance.value = await getReCaptcha(userStore.selectedLanguage);
});

onBeforeUnmount(() => {
  //Hide the badge after the component is destroyed so it doesn't show on every page
  recaptchaInstance.value?.hideBadge();
});

watch(
  () => userStore.$state.selectedLanguage,
  async languageChanged => {
    updateReCaptchaLanguage(languageChanged);
  }
);

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${baseComponentName}.${v}`, params);
}

const emailInvalidCharacterError = computed(() => {
  return reportStringErrors(
    userMail.value,
    t("Common.email"),
    MIN_EMAIL_LENGTH,
    MAX_EMAIL_LENGTH,
    emailCharactersRegex
  );
});

const emailError = computed(() => {
  if (!dirtyFlagEmail.value) return null;
  const errs = isValidEmail(userMail.value).filter(e => !e.passed);
  if (errs.length > 0) return errs[0];
  if (emailInvalidCharacterError.value.length > 0) {
    return { name: "invalidCharacter", message: emailInvalidCharacterError.value } as IValidationResult;
  }
  if (aolAccountStore.isSmbEmail || aolAccountStore.isNonEmptyAccount) {
    return { name: "invalidCharacter", message: t("BuyEmailComponent.differentEmail") } as IValidationResult;
  }
  return null;
});

function captchaV2Callback(responseToken: string) {
  logEvent(`Captcha token: ${responseToken}`, `${componentName}/captchaV2Callback`);
  captchaV2ResponseToken.value = responseToken;
  checkUserEmail();
}

function onCaptchaV2Expired() {
  logEvent("Captcha expired", `${componentName}/onCaptchaV2Expired`);
  captchaV2ResponseToken.value = "";
}

async function checkUserEmail() {
  isVerifying.value = true;
  dirtyFlagEmail.value = true;
  if (emailError.value) {
    isVerifying.value = false;
  } else {
    showV2Recaptcha.value = await checkForUserAccount(
      userMail,
      showV2Recaptcha,
      captchaV2ResponseToken,
      recaptchaInstance,
      componentName
    );
  }
  isVerifying.value = false;
}
</script>
